/**
 * Payout Management Dashboard
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Manages rent and HOA fee payouts using:
 * - Stripe Global Payouts (for supported countries)
 * - Manual payouts (for unsupported regions)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DollarSign, Send, Clock, CheckCircle, XCircle, AlertTriangle,
  Building2, Home, Users, CreditCard, Banknote, Globe, Search,
  Filter, Download, RefreshCw, Plus, Eye, Edit, Trash2, ArrowRight,
  TrendingUp, Calendar, FileText, Landmark, User, Mail, Phone, MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stripe supported countries
const STRIPE_COUNTRIES = [
  'US', 'GB', 'AU', 'CA', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'IE',
  'PT', 'FI', 'SE', 'DK', 'NO', 'CH', 'NZ', 'SG', 'HK', 'JP', 'MX', 'BR',
  'IN', 'PH', 'ZA', 'KE', 'NG', 'GH', 'JM', 'TT', 'BS', 'BB'
];

const PayoutManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showNewPayoutDialog, setShowNewPayoutDialog] = useState(false);
  const [showRecipientDialog, setShowRecipientDialog] = useState(false);
  const [showMarkCompleteDialog, setShowMarkCompleteDialog] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: ''
  });
  
  // New payout form
  const [newPayout, setNewPayout] = useState({
    recipient_id: '',
    amount: '',
    currency: 'USD',
    payout_type: 'rent',
    description: ''
  });
  
  // New recipient form
  const [newRecipient, setNewRecipient] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'US',
    type: 'landlord',
    bank_name: '',
    account_number: '',
    routing_number: ''
  });
  
  // Mark complete form
  const [completeForm, setCompleteForm] = useState({
    reference_number: '',
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [summaryRes, pendingRes, allRes, recipientsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/payouts/summary`, { headers }),
        axios.get(`${BACKEND_URL}/api/payouts/pending-manual`, { headers }),
        axios.get(`${BACKEND_URL}/api/payouts`, { headers }),
        axios.get(`${BACKEND_URL}/api/payouts/recipients`, { headers })
      ]);
      
      setPayoutSummary(summaryRes.data);
      setPendingPayouts(pendingRes.data.payouts || []);
      setAllPayouts(allRes.data.payouts || []);
      setRecipients(recipientsRes.data.recipients || []);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreatePayout = async () => {
    if (!newPayout.recipient_id || !newPayout.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payouts/create`,
        newPayout,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Payout created successfully!');
        setShowNewPayoutDialog(false);
        setNewPayout({ recipient_id: '', amount: '', currency: 'USD', payout_type: 'rent', description: '' });
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to create payout');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payout');
    }
  };

  const handleCreateRecipient = async () => {
    if (!newRecipient.name || !newRecipient.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payouts/recipients`,
        newRecipient,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Recipient added successfully!');
        setShowRecipientDialog(false);
        setNewRecipient({ name: '', email: '', phone: '', country: 'US', type: 'landlord', bank_name: '', account_number: '', routing_number: '' });
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to add recipient');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add recipient');
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedPayout) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/payouts/${selectedPayout.id}/complete`,
        completeForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Payout marked as completed!');
        setShowMarkCompleteDialog(false);
        setSelectedPayout(null);
        setCompleteForm({ reference_number: '', notes: '' });
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to update payout');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payout');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-700', icon: XCircle },
      manual_required: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
      cancelled: { color: 'bg-gray-100 text-gray-700', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPayoutTypeBadge = (type) => {
    const typeConfig = {
      rent: { color: 'bg-emerald-100 text-emerald-700', icon: Home, label: 'Rent' },
      hoa_fee: { color: 'bg-blue-100 text-blue-700', icon: Building2, label: 'HOA Fee' },
      referral_commission: { color: 'bg-purple-100 text-purple-700', icon: Users, label: 'Referral' },
      security_deposit_return: { color: 'bg-amber-100 text-amber-700', icon: DollarSign, label: 'Deposit Return' },
      other: { color: 'bg-gray-100 text-gray-700', icon: FileText, label: 'Other' }
    };
    
    const config = typeConfig[type] || typeConfig.other;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const isStripeSupported = (country) => STRIPE_COUNTRIES.includes(country);

  const filteredPayouts = allPayouts.filter(payout => {
    if (filters.status !== 'all' && payout.status !== filters.status) return false;
    if (filters.type !== 'all' && payout.payout_type !== filters.type) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        payout.description?.toLowerCase().includes(search) ||
        payout.recipient?.name?.toLowerCase().includes(search) ||
        payout.id?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Send className="h-6 w-6 text-emerald-600" />
            Payout Management
          </h2>
          <p className="text-gray-500">Manage rent and HOA fee payouts via Stripe or manual processing</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowRecipientDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Recipient
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowNewPayoutDialog(true)}>
            <Send className="h-4 w-4 mr-2" />
            New Payout
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{payoutSummary?.pending || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Processing</p>
                <p className="text-2xl font-bold text-blue-600">{payoutSummary?.processing || 0}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Manual Required</p>
                <p className="text-2xl font-bold text-orange-700">{payoutSummary?.manual_required || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{payoutSummary?.completed || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-700">
                  ${((payoutSummary?.total_completed_amount || 0) / 100).toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="manual" className="relative">
            Manual Payouts
            {(payoutSummary?.manual_required || 0) > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {payoutSummary?.manual_required}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="all">All Payouts</TabsTrigger>
          <TabsTrigger value="recipients">Recipients</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Stripe Payouts Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  Stripe Global Payouts
                </CardTitle>
                <CardDescription>Automated payouts to 58+ countries</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Supported Features:</h4>
                  <ul className="space-y-1 text-sm text-blue-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Standard payouts (2-3 business days)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Wire transfers (1 business day)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Instant payouts to debit cards (US only)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Local currency conversion
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Supported Countries Include:</h4>
                  <p className="text-sm text-gray-600">
                    US, UK, Canada, Australia, Germany, France, Jamaica, Trinidad & Tobago, 
                    Bahamas, Barbados, and 50+ more countries.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Manual Payouts Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="h-5 w-5 text-orange-600" />
                  Manual Payouts
                </CardTitle>
                <CardDescription>For unsupported regions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-2">Manual Processing Required For:</h4>
                  <ul className="space-y-1 text-sm text-orange-700">
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Haiti
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Cuba (if applicable)
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Other Caribbean nations not on Stripe
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Manual Payout Methods:</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <Badge variant="outline">Bank Transfer</Badge>
                    <Badge variant="outline">Check</Badge>
                    <Badge variant="outline">Cash</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Payouts */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Payouts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allPayouts.slice(0, 5).map((payout) => (
                  <div key={payout.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      {getPayoutTypeBadge(payout.payout_type)}
                      <div>
                        <p className="font-medium">{payout.recipient?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{payout.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold">${(payout.amount / 100).toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{payout.currency}</p>
                      </div>
                      {getStatusBadge(payout.status)}
                    </div>
                  </div>
                ))}
                {allPayouts.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No payouts yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Payouts Tab */}
        <TabsContent value="manual" className="space-y-6">
          <Card className="border-orange-200">
            <CardHeader className="bg-orange-50">
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <AlertTriangle className="h-5 w-5" />
                Payouts Requiring Manual Processing
              </CardTitle>
              <CardDescription>
                These payouts are to recipients in unsupported regions. Process them manually and mark as complete.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {pendingPayouts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">No manual payouts pending!</p>
                </div>
              ) : (
                <div className="divide-y">
                  {pendingPayouts.map((payout) => (
                    <div key={payout.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-orange-100 p-3 rounded-full">
                            <Banknote className="h-6 w-6 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{payout.recipient?.name || 'Unknown Recipient'}</p>
                            <p className="text-sm text-gray-500">{payout.recipient?.email}</p>
                            <p className="text-sm text-gray-500">
                              <MapPin className="h-3 w-3 inline mr-1" />
                              {payout.recipient?.country || 'Unknown'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            {getPayoutTypeBadge(payout.payout_type)}
                            <p className="text-2xl font-bold mt-1">${(payout.amount / 100).toFixed(2)}</p>
                            <p className="text-sm text-gray-500">{payout.description}</p>
                          </div>
                          <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setShowMarkCompleteDialog(true);
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Complete
                          </Button>
                        </div>
                      </div>
                      {payout.bank_details && (
                        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                          <h4 className="font-medium text-sm mb-2">Bank Details:</h4>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Bank:</span>
                              <span className="ml-2">{payout.bank_details.bank_name || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Account:</span>
                              <span className="ml-2">****{payout.bank_details.account_last4 || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Routing:</span>
                              <span className="ml-2">{payout.bank_details.routing_number || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Payouts Tab */}
        <TabsContent value="all" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search payouts..."
                      className="pl-10"
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                    />
                  </div>
                </div>
                <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="manual_required">Manual Required</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.type} onValueChange={(v) => setFilters({...filters, type: v})}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="hoa_fee">HOA Fee</SelectItem>
                    <SelectItem value="referral_commission">Referral</SelectItem>
                    <SelectItem value="security_deposit_return">Deposit Return</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Payouts Table */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-medium">Recipient</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Amount</th>
                    <th className="text-left p-4 font-medium">Method</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Date</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPayouts.map((payout) => (
                    <tr key={payout.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{payout.recipient?.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{payout.recipient?.email}</p>
                        </div>
                      </td>
                      <td className="p-4">{getPayoutTypeBadge(payout.payout_type)}</td>
                      <td className="p-4 font-bold">${(payout.amount / 100).toFixed(2)}</td>
                      <td className="p-4">
                        <Badge variant="outline">
                          {payout.payout_method?.replace('_', ' ') || 'N/A'}
                        </Badge>
                      </td>
                      <td className="p-4">{getStatusBadge(payout.status)}</td>
                      <td className="p-4 text-sm text-gray-500">
                        {new Date(payout.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredPayouts.length === 0 && (
                <p className="text-center text-gray-500 py-12">No payouts found</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recipients Tab */}
        <TabsContent value="recipients" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Payout Recipients</CardTitle>
              <CardDescription>Landlords, HOAs, and partners eligible to receive payouts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recipients.map((recipient) => (
                  <Card key={recipient.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-emerald-100 p-2 rounded-full">
                            <User className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{recipient.name}</p>
                            <Badge variant="outline" className="text-xs capitalize">
                              {recipient.type}
                            </Badge>
                          </div>
                        </div>
                        {isStripeSupported(recipient.country) ? (
                          <Badge className="bg-green-100 text-green-700">Stripe</Badge>
                        ) : (
                          <Badge className="bg-orange-100 text-orange-700">Manual</Badge>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p className="flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          {recipient.email}
                        </p>
                        <p className="flex items-center gap-2">
                          <MapPin className="h-3 w-3" />
                          {recipient.country}
                        </p>
                        {recipient.bank_name && (
                          <p className="flex items-center gap-2">
                            <Landmark className="h-3 w-3" />
                            {recipient.bank_name} ****{recipient.account_last4}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" size="sm" className="flex-1">
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            setNewPayout({...newPayout, recipient_id: recipient.id});
                            setShowNewPayoutDialog(true);
                          }}
                        >
                          <Send className="h-3 w-3 mr-1" />
                          Pay
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {recipients.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No recipients added yet</p>
                    <Button 
                      className="mt-4"
                      onClick={() => setShowRecipientDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Recipient
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Payout Dialog */}
      <Dialog open={showNewPayoutDialog} onOpenChange={setShowNewPayoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Payout</DialogTitle>
            <DialogDescription>Send money to a recipient via Stripe or manual processing</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recipient *</Label>
              <Select 
                value={newPayout.recipient_id} 
                onValueChange={(v) => setNewPayout({...newPayout, recipient_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select recipient" />
                </SelectTrigger>
                <SelectContent>
                  {recipients.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} ({r.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    type="number"
                    className="pl-10"
                    placeholder="0.00"
                    value={newPayout.amount}
                    onChange={(e) => setNewPayout({...newPayout, amount: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Currency</Label>
                <Select 
                  value={newPayout.currency} 
                  onValueChange={(v) => setNewPayout({...newPayout, currency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="JMD">JMD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Payout Type *</Label>
              <Select 
                value={newPayout.payout_type} 
                onValueChange={(v) => setNewPayout({...newPayout, payout_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">Rent Payment</SelectItem>
                  <SelectItem value="hoa_fee">HOA Fee</SelectItem>
                  <SelectItem value="referral_commission">Referral Commission</SelectItem>
                  <SelectItem value="security_deposit_return">Security Deposit Return</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                placeholder="e.g., January 2026 rent for Unit 4A"
                value={newPayout.description}
                onChange={(e) => setNewPayout({...newPayout, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPayoutDialog(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleCreatePayout}>
              <Send className="h-4 w-4 mr-2" />
              Create Payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recipient Dialog */}
      <Dialog open={showRecipientDialog} onOpenChange={setShowRecipientDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Payout Recipient</DialogTitle>
            <DialogDescription>Add a landlord, HOA, or partner to receive payouts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input 
                  placeholder="John Doe"
                  value={newRecipient.name}
                  onChange={(e) => setNewRecipient({...newRecipient, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  placeholder="john@example.com"
                  value={newRecipient.email}
                  onChange={(e) => setNewRecipient({...newRecipient, email: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input 
                  placeholder="+1 555-0123"
                  value={newRecipient.phone}
                  onChange={(e) => setNewRecipient({...newRecipient, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Country *</Label>
                <Select 
                  value={newRecipient.country} 
                  onValueChange={(v) => setNewRecipient({...newRecipient, country: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="JM">Jamaica</SelectItem>
                    <SelectItem value="TT">Trinidad & Tobago</SelectItem>
                    <SelectItem value="BS">Bahamas</SelectItem>
                    <SelectItem value="BB">Barbados</SelectItem>
                    <SelectItem value="HT">Haiti</SelectItem>
                    <SelectItem value="GY">Guyana</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Recipient Type</Label>
              <Select 
                value={newRecipient.type} 
                onValueChange={(v) => setNewRecipient({...newRecipient, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landlord">Landlord</SelectItem>
                  <SelectItem value="hoa">HOA/Condo Association</SelectItem>
                  <SelectItem value="property_manager">Property Manager</SelectItem>
                  <SelectItem value="partner">Referral Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Bank Account Details (Optional)</h4>
              <div className="space-y-3">
                <div>
                  <Label>Bank Name</Label>
                  <Input 
                    placeholder="e.g., Chase Bank"
                    value={newRecipient.bank_name}
                    onChange={(e) => setNewRecipient({...newRecipient, bank_name: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Account Number</Label>
                    <Input 
                      placeholder="****1234"
                      value={newRecipient.account_number}
                      onChange={(e) => setNewRecipient({...newRecipient, account_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Routing Number</Label>
                    <Input 
                      placeholder="021000021"
                      value={newRecipient.routing_number}
                      onChange={(e) => setNewRecipient({...newRecipient, routing_number: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {!isStripeSupported(newRecipient.country) && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-700">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  <strong>Note:</strong> {newRecipient.country} is not supported by Stripe. 
                  Payouts to this recipient will require manual processing.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecipientDialog(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleCreateRecipient}>
              <Plus className="h-4 w-4 mr-2" />
              Add Recipient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Complete Dialog */}
      <Dialog open={showMarkCompleteDialog} onOpenChange={setShowMarkCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Payout as Complete</DialogTitle>
            <DialogDescription>
              Confirm that you have manually processed this payout
            </DialogDescription>
          </DialogHeader>
          {selectedPayout && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Recipient:</span>
                    <span className="ml-2 font-medium">{selectedPayout.recipient?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Amount:</span>
                    <span className="ml-2 font-bold">${(selectedPayout.amount / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label>Reference Number (Optional)</Label>
                <Input 
                  placeholder="Bank transfer reference, check number, etc."
                  value={completeForm.reference_number}
                  onChange={(e) => setCompleteForm({...completeForm, reference_number: e.target.value})}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  className="w-full px-3 py-2 border rounded-md"
                  rows={3}
                  placeholder="Any additional notes about this payout..."
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm({...completeForm, notes: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkCompleteDialog(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleMarkComplete}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayoutManagement;
